import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness.js';
import { TiptapCollabProvider, WebSocketStatus } from '@tiptap-pro/provider';
import { useThrottledCallback } from '@mantine/hooks';
import ReplayIcon from '@mui/icons-material/Replay';
import ErrorAlerts, { ErrorDetails } from 'components/generic/ErrorAlerts';
import SkeletonBox from 'components/generic/SkeletonBox';
import {
  NxtYArrConfig,
  NxtYDocConfig,
} from 'components/RichTextEditor/hooks/useCreateEditor';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useCosmosRuntime } from 'providers/contexts/CosmosRuntimeContext';
import {
  deleteAssetIdLockKeepalive,
  NXT_LOCK_ORIGIN,
} from 'providers/contexts/extensions/Editorial';
import { useAppSettings } from 'providers/contexts/SettingsContext';
import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { EditContext, useDataProvider, useRecordContext } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { clone, isDeepEqual, isPlainObject } from 'remeda';
import { emdash } from 'utility/constants';
import { hasCollabExp } from 'utility/experiments';
import {
  checkModifyAssetsPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';
import { YKeyValue } from 'y-utility/y-keyvalue';
import { AssetType } from '../../../../utility/types/cosmosTypes';
import type { AssetQuickLaunchPrefill } from 'components/editorial/AssetQuickLaunch/types';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  DeleteEditorialAssetIdLockParams,
  DeleteEditorialAssetIdLockResult,
  FullEditorialAsset,
  GetEditorialAssetIdLockResult,
  PutEditorialAssetIdLockParams,
  PutEditorialAssetIdLockResult,
} from '../../../../utility/types/dataProvider';
import { AssetLocked } from '../components/generic/AssetLocked';
import {
  AssetFormValuesType,
  CollabProviderState,
  createAssetDefaultValues,
  formatAssetFormDefaultValues,
  resolveAssetPrefillValues,
  initReadOnlyState,
  isAssetLocked,
  isAssetLockedByNXT,
  isAssetLockedByUser,
  NXT_YDOC_CONFIG,
  ReadOnlyState,
  SyncedInitialYDocConfigState,
} from './AssetConfigUtilities';
import {
  buildCollabUserInfo,
  buildCollabUserPresenceInfo,
  CollabUserInfo,
  TiptapAwarenessInfo,
  AwarenessUpdateInfo,
} from '../presence/useDocumentPresence';
import { Workflow, WorkflowProcess } from 'providers/contexts/extensions/Workflows';
import { useCosmosEditorial } from 'providers/contexts/CosmosEditorialContext';
import { isAssetActuallyDeleted } from '../utility/assetFunctions';

/**
 *  Do not use ID - the actual ID of the asset is the UUID.
 *  ID is here for react-admin to stop complaining about the type in their hooks,
 *  and it it's not actually present in our API response
 *  We generally copy the UUID over to the ID field in the dataProvider
 *  but to be safe just directly use the asset UUID instead.
 **/
export type AssetDataResponse = {
  data: Partial<FullEditorialAsset>;
  status: string;
  id: string;
};

// undefined until the state is determined
export type AssetUpdateModeState = 'edit' | 'create' | 'import' | undefined;

export type AssetConfigContextProps = {
  mode: AssetUpdateModeState;
  children?: React.ReactNode;
  createAssetType?: AssetType | string;
  createAssetPrefill?: AssetQuickLaunchPrefill;
  createAssetIsTemplate?: boolean;
};

export type AssetConfigContextValuesType = {
  // Asset data and mode
  recordCopy: FullEditorialAsset | undefined;
  prevRecordCopy: FullEditorialAsset | undefined;
  assetFormDefaultValues: AssetFormValuesType | undefined;
  recordType: AssetType | string;
  assetUpdateMode: AssetUpdateModeState;
  isEditingMode: boolean;
  isImportMode: boolean;
  isCreateMode: boolean;
  // Tiptap Collaboration
  collabProvider?: CollabProviderState;
  yDoc: Y.Doc; // ydoc instance
  collabUser: CollabUserInfo; // basic user info for tiptap extensions
  providerSynced: boolean; // live synced state (not initial sync)
  providerStatus: WebSocketStatus; // live connection status (connecting, connected, disconnected)
  // Need a snapshot of the initial ydoc config map for first collab onsynced
  syncedInitialYDocConfig: SyncedInitialYDocConfigState;
  // Workflows - see notes, states are synced programmatically
  workflowId: string | null;
  setWorkflowId: React.Dispatch<React.SetStateAction<string | null>>;
  workflowProcessId: string | null;
  setWorkflowProcessId: React.Dispatch<React.SetStateAction<string | null>>;
  // The current workflow or process...
  currentWorkflow: Workflow | null;
  currentWorkflowProcess: WorkflowProcess | null;
  // Current workflow group (null if no wf/proc/group)
  currentProcessGroup: string | null;
  // Is user in the current workflow group? (true if no wf/proc/group)
  inCurrentProcessGroup: boolean;
  // ReadOnly Mode
  readOnlyState: ReadOnlyState;
  setReadOnlyState: React.Dispatch<React.SetStateAction<ReadOnlyState>>;
  isReadOnlyMode: boolean; // convenience via readOnlyState
  // Template Mode
  isTemplate: boolean;
  // Deleted Mode
  isDeleted: boolean;
  // Collaboration Mode
  isCollabMode: boolean; // true when collaboration is active (has provider and synced)
};

/**
 * Helper to get a new Y.Doc instance, for useState initialization.
 * https://react.dev/reference/react/useState#avoiding-recreating-the-initial-state
 */
function getNewYDoc(): Y.Doc {
  const ydoc = new Y.Doc();
  return ydoc;
}

// -----------------------------------------------------------------
// Context Provider and Hooks
// -----------------------------------------------------------------

export const AssetConfigContext = createContext<AssetConfigContextValuesType | undefined>(
  undefined,
);

export const useAssetConfigContext = () => {
  const context = useContext(AssetConfigContext);
  if (!context) {
    throw new Error(
      'useAssetConfigContext must be used within an AssetConfigContextProvider',
    );
  }
  return context;
};

export const AssetConfigContextProvider = memo(
  ({
    children,
    createAssetType,
    mode,
    createAssetPrefill,
    createAssetIsTemplate,
  }: AssetConfigContextProps) => {
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const { t } = useTranslation();
    const {
      appSettings: { site: siteSettings, global: globalSettings },
    } = useAppSettings();
    const { sessionConfig, userInfo, userGroups } = useCosmos();
    const siteTimeZone = sessionConfig?.time_zone;
    const { runtimeId } = useCosmosRuntime();
    const { siteWorkflows, editorialConfig } = useCosmosEditorial();
    const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
    const { granted: userCanModify, name: modifyPermName } =
      checkModifyAssetsPermission(sessionConfig);

    const userUuid = userInfo?.uuid;

    // Read Only Mode (no permissions, is locked, etc)
    const [readOnlyState, setReadOnlyState] = useState<ReadOnlyState>(initReadOnlyState);
    const isReadOnlyMode = readOnlyState?.isReadOnly;

    // In RA v5, useRecordContext returns the record directly when available.
    // In create mode it is undefined until defaults are set; in edit mode it populates from the API fetch.
    const recordContextData = useRecordContext<AssetDataResponse>();

    // Read EditContext directly so create mode can safely render without an EditContextProvider.
    const editContext = useContext(EditContext);
    const { isLoading: editCtxIsLoading, error: editCtxError } = editContext ?? {};

    // Record data copied from react-admin context
    // Note: The actual data of the asset is in recordContext.data.
    // The record could still be fetching/loading and not ready at mount time.
    // So we cant rely on useRecordContext safely in all cases.
    // In create mode we will set it to same as the form default values
    // but in edit mode, it will be whatever was fetched from the api.
    const [recordCopy, setRecordCopy] = useState<FullEditorialAsset | undefined>(
      undefined,
    );
    const recordType = recordCopy?.type || '';
    const recordUuid = recordCopy?.uuid || '';
    const recordRevId = recordCopy?.properties?.current_revision?.id;

    // Keep the previously loaded record state for comparison purposes
    const [prevRecordCopy, setPrevRecordCopy] = useState<FullEditorialAsset | undefined>(
      undefined,
    );

    const prevRecordUuid = prevRecordCopy?.uuid || '';

    // Update the current record copy and the previous record copy together.
    // Note: since both states start out undefined, on the initial setting
    // of the record copies, the previous record will remain undefined.
    // You can use this to detect the initial copy of the record in edit mode.
    // note: if you are using the previous record state for any first load init
    // checks (eg prev.id !== curr.id) inside an effect, dont accidentally
    // create an infinite loop if the effect updates any states that it depends on
    // causing it to re-run and hit that if statement over and over again.
    // Also do not make the assumption that the previous record will truly
    // be the "previous" record -- it is only what we loaded into state
    // whenever the fetch occured, someone else may have made saves in between,
    // so upon our save, we might get some newer data back from the API...
    // For example, if the current revision is 1, dont assume that
    // upon our save the new revision will be 2, it could be 3, 4, 12, etc.
    const updateRecordCopies = useCallback(
      (newRecord: FullEditorialAsset | undefined) => {
        setPrevRecordCopy(recordCopy); // Prev to current state
        setRecordCopy(newRecord); // New record state
      },
      [recordCopy, setRecordCopy, setPrevRecordCopy],
    );

    // Determine edit or create mode via prop, update in effect...
    const [assetUpdateMode, setAssetUpdateMode] =
      useState<AssetUpdateModeState>(undefined);
    const isCreateMode = assetUpdateMode === 'create';
    const isEditingMode = assetUpdateMode === 'edit';
    const isImportMode = assetUpdateMode === 'import';

    // React Admin EditBase was causing loops of refetch/redirect on fetch error
    // eg an invalid uuid in the url, so we need to handle the error here.
    // Please see notes in AssetEditView onError handler about RA side effects...
    // In create mode error ctx should be falsy as it is not in an ra edit ctx;
    // however in edit or import mode if a fetch errors it should be truthy.
    const hasEditCtxError = Boolean(editCtxError);
    const raEditError = editCtxError as ApiErrorResponse | undefined;

    // Determine mode based on params and setup starting states.
    useEffect(() => {
      // In create mode we dont need to await any record
      // so we can just set the mode and setup form defaults.
      // await global settings and user info for adding defaults
      // only set the initial record once - check if recordCopy is set
      // else if user changes globalSettings this effect will rerun
      // and some of the form may be reset and cleared
      if (
        mode === 'create' &&
        createAssetType &&
        !recordCopy &&
        globalSettings &&
        userInfo
      ) {
        // Set mode if not already correct
        if (assetUpdateMode !== 'create') {
          setAssetUpdateMode('create');
        }

        // Resolve create prefill via various sources
        // eg: self default author, profile settings values,
        // quick launch modal args, site settings, etc...
        const prefill = resolveAssetPrefillValues({
          globalSettings: globalSettings,
          userInfo: userInfo,
          siteSettings: siteSettings,
          siteWorkflows: siteWorkflows,
          createAssetPrefill: createAssetPrefill,
          disablePhotoSales: editorialConfig?.asset_config?.image?.disablePhotoSales,
          assetType: createAssetType,
        });

        // in create mode the defaults can be treated as the initial 'record'
        // set the state so assetFormDefaultValues variable can be computed from it.
        // it should not update as we redirect from /create to /edit/:id anyways.
        const createFormDefaultValues = createAssetDefaultValues({
          type: createAssetType,
          authors: prefill.authors,
          startTime: prefill.startTime,
          workflowId: prefill.workflowId,
          workflowProcessId: prefill.workflowProcessId,
          slug: prefill.slug,
          title: prefill.title,
          photoSales: prefill.photoSales,
          presentation: prefill.presentation,
          isTemplate: createAssetIsTemplate,
        });

        // Set the formatted record copy to the state
        updateRecordCopies(createFormDefaultValues);
      }

      // Within edit/import mode we need to wait for the record to finish fetching.
      if (mode === 'edit' || mode === 'import') {
        // Wire/Syndication Import Mode - always read only
        if (assetUpdateMode !== 'import' && mode === 'import') {
          setAssetUpdateMode('import');
          const roStr = t('blox.general.readonly');
          const roReason = t('blox.general.import_mode');
          const roMsg = `${roStr} ${emdash} ${roReason}`;
          setReadOnlyState({
            isReadOnly: true,
            message: roMsg,
            reason: 'permissions',
          });
        }

        // set modes and perms if not already correct
        if (assetUpdateMode !== 'edit' && mode === 'edit') {
          setAssetUpdateMode('edit');
          // note: editorial "read only" (permissions) setup
          // has been moved down near workflows setup as it
          // also depends on workflow/process group states.
        }

        // Wait for record to finish loading (isLoading / isFetching)
        if (editCtxIsLoading) {
          return;
        }

        // If error in the edit context log and return.
        if (editCtxError) {
          console.error('[AssetConfigContext] Error:', editCtxError);
          return;
        }

        // Data should have been fetched in full without error...
        const fetchedRecord = recordContextData?.data;

        // If somehow record is missing/invalid log and return.
        if (!fetchedRecord || !isPlainObject(fetchedRecord)) {
          console.warn('[AssetConfigContext] No record fetched:', fetchedRecord);
          return;
        }

        // Place initial record data into local states and update if changes.
        // In edit mode should only happen when there are durable changes like a blue save.
        const shouldUpdateRecordCopy = !isDeepEqual(fetchedRecord, recordCopy);
        if (shouldUpdateRecordCopy) {
          updateRecordCopies(fetchedRecord);
        }
      }
    }, [
      t,
      globalSettings,
      recordCopy,
      updateRecordCopies,
      assetUpdateMode,
      editCtxError,
      editCtxIsLoading,
      createAssetPrefill,
      createAssetType,
      createAssetIsTemplate,
      editorialConfig,
      mode,
      recordContextData?.data,
      siteSettings,
      siteWorkflows,
      userInfo,
    ]);

    // Compute default values for the form state...
    // Should be undefined until record state is set
    // https://marmelab.com/react-admin/Form.html#defaultvalues
    // https://react-hook-form.com/docs/useform
    const assetFormDefaultValues: AssetFormValuesType | undefined = useMemo(() => {
      // Perform some formatting in certain modes in order to format fields
      // eg authors, custom_ props, children... for the form default values
      if (assetUpdateMode === 'edit' && recordCopy) {
        const defaultValues = formatAssetFormDefaultValues(recordCopy);
        return defaultValues;
      }

      if (assetUpdateMode === 'create' && recordCopy) {
        const defaultValues = formatAssetFormDefaultValues(recordCopy);
        return defaultValues;
      }

      if (assetUpdateMode === 'import' && recordCopy) {
        const defaultValues = formatAssetFormDefaultValues(recordCopy);
        return defaultValues;
      }

      return undefined;
    }, [assetUpdateMode, recordCopy]);

    // -----------------------------------------------------------------
    // Collaboration Pre-Checks
    // -----------------------------------------------------------------

    // Keys and Settings for Collaboration
    const hpAppId = sessionConfig?.service_keys?.tiptap_hocuspocus_app;
    const hpToken = sessionConfig?.service_keys?.tiptap_hocuspocus_token;

    // Do we have the prereqs for collab mode?
    // Note: this does not neccesarily mean we will connect to
    // the provider, we only connect if we are not in a lockout state...
    // In the future can setup a readonly mode, rather than a full lockout then
    // adjust the checks to allow connecting but in a non-editable state
    // Will need to adjust the useCreateEditor init content setup + editable props
    // states so the readonly users dont overwrite the ydoc upon connecting
    const hasCollabPrereqs = useMemo(() => {
      return (
        hasCollabExp(sessionConfig) &&
        assetUpdateMode === 'edit' &&
        recordType === 'article' &&
        Boolean(recordUuid) &&
        Boolean(userUuid) &&
        Boolean(hpAppId) &&
        Boolean(hpToken)
      );
    }, [
      assetUpdateMode,
      hpAppId,
      hpToken,
      recordType,
      recordUuid,
      sessionConfig,
      userUuid,
    ]);

    // -----------------------------------------------------------------
    // Asset Lock Checks - Unlocking will be further down
    // -----------------------------------------------------------------

    // Run locking checks only in edit mode once the record is ready
    const assetLockQueryEnabled = Boolean(recordUuid) && isEditingMode;

    // query - check asset lock
    const assetLockQuery = useQuery<
      GetEditorialAssetIdLockResult,
      ApiErrorResponse,
      GetEditorialAssetIdLockResult['data']
    >({
      queryKey: ['getAssetIdLock', recordUuid],
      queryFn: async () => {
        const result = await dataProvider.getAssetIdLock({ path: { id: recordUuid } });
        return result;
      },
      enabled: assetLockQueryEnabled,
      select: (data) => data?.data,
      staleTime: 0,
      gcTime: 0, // no bg auto refetching
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchInterval: false,
      refetchIntervalInBackground: false,
    });

    const {
      data: assetLockQueryData,
      status: assetLockQueryStatus,
      error: assetLockQueryError,
      refetch: refetchAssetLockQuery,
    } = assetLockQuery;

    const lockQuerySuccess = assetLockQueryStatus === 'success';
    const isLockedAnywhere = lockQuerySuccess && isAssetLocked(assetLockQueryData);
    const isLockedByNXT = lockQuerySuccess && isAssetLockedByNXT(assetLockQueryData);
    const isLockedByThisUser =
      lockQuerySuccess && isAssetLockedByUser(assetLockQueryData, userUuid);

    // Collaboration on and lock is not held by NXT
    const collabModeLockout = hasCollabPrereqs && !isLockedByNXT;

    // Collaboration off and lock is held by someone else or in another environment
    const nonCollabModeLockout =
      !hasCollabPrereqs && (!isLockedByNXT || !isLockedByThisUser);

    // If either lockout state is true...
    const hasAnyLockout = collabModeLockout || nonCollabModeLockout;

    // User continued in read-only mode from lockout interstitial?
    const bypassedLockout = readOnlyState.reason === 'lockout_view';

    // Show the lockout interstitial or continue to editor (as read-only mode)
    const showLockout = isLockedAnywhere && hasAnyLockout && !bypassedLockout;

    // Track if initial locking process has ran for this record
    // shoud only be true after first lock + maybe acq checks are done
    const [hasRanInitLockCheck, setHasRanInitLockCheck] = useState(false);

    // If asset lock process is loading for skeleton UI
    // in future expand to account for acquire lock states as well
    const lockingProcessLoading = assetLockQueryEnabled && !hasRanInitLockCheck;

    // Asset Locking Mutations
    const assetLockMutation = useMutation<
      PutEditorialAssetIdLockResult,
      ApiErrorResponse,
      PutEditorialAssetIdLockParams
    >({
      mutationFn: (params) => dataProvider.putAssetIdLock(params),
      retry: false,
    });

    const {
      mutateAsync: assetLockMutationFnAsync,
      status: lockMutationStatus,
      error: lockMutationError,
    } = assetLockMutation;

    const assetUnlockMutation = useMutation<
      DeleteEditorialAssetIdLockResult,
      ApiErrorResponse,
      DeleteEditorialAssetIdLockParams
    >({
      mutationFn: (params) => dataProvider.deleteAssetIdLock(params),
      retry: false,
    });

    const { mutateAsync: assetUnlockMutationFnAsync, status: unlockMutationStatus } =
      assetUnlockMutation;

    // Prevent multiple lock acquisition attempts inflight
    // useEffect was running multiple times even when
    // checking the lockMutationStatus directly...
    const isAcquiringLock = useRef(false);

    /**
     * Note on tryAcqLockAndRefetch:
     * this comment may not be applicable anymore after TNCMS-555797:
     * rare edge case - if multiple users try to acquire a lock simultaneously
     * since these are async operations one will get the lock and others will get a 400
     * as the server would see another user trying to 'put' over an existing lock.
     * Whoever the server decides to process first will get the lock, the other clients
     * would recieve an error. Best idea to handle this without creating a loop of refetching
     * is to show the error component with button to rerun the process.
     */

    // try to acquire lock and refetch lock data
    const tryAcqLockAndRefetch = useCallback(async () => {
      try {
        // track via ref in progress to prevent multiple calls
        isAcquiringLock.current = true;
        // attempt to acquire lock
        await assetLockMutationFnAsync({
          path: { id: recordUuid },
          body: { origin: NXT_LOCK_ORIGIN },
        });
        // give api a moment to breathe and setup things...
        await new Promise((resolve) => setTimeout(resolve, 250));
        // refetch asset lock state after acquiring
        const result = await refetchAssetLockQuery();
        // return result for convenience
        return result;
      } catch (error) {
        // If anything throws log and pass error up the chain
        console.error('[tryAcqLockAndRefetch] Error:', error);
        throw error;
      } finally {
        // reset ref when attempt is complete
        isAcquiringLock.current = false;
      }
    }, [assetLockMutationFnAsync, recordUuid, refetchAssetLockQuery]);

    // Run lock check and maybe acquire
    useEffect(() => {
      // setup lock checks only once per editor load
      if (hasRanInitLockCheck) {
        return;
      }
      // await record ready and lock query done
      if (!recordUuid || !lockQuerySuccess) {
        return;
      }
      // if errored show error ui and return
      if (lockMutationStatus === 'error') {
        return;
      }
      // Acq lock if asset unlocked or locked by NXT in collab mode
      // Try to acquire/add a lock if:
      // Asset is not locked anywhere OR
      // Asset is locked by NXT in collab mode (eg: new user incoming to collab)
      const handleInitLockAcquire = async () => {
        // lock mutation is still loading...
        if (lockMutationStatus === 'pending') {
          return;
        }
        // prevent multiple acquire lock calls
        if (isAcquiringLock?.current === true) {
          return;
        }
        try {
          const addCollabLock = hasCollabPrereqs && isLockedByNXT;
          const shouldTryAcqLock = !isLockedAnywhere || addCollabLock;
          if (shouldTryAcqLock) {
            await tryAcqLockAndRefetch();
          }
        } catch (error) {
          console.error('[handleInitLockAcquire] Error:', error);
        } finally {
          // set check state - does not indicate success or failure
          // only indicates attempts have been made to check/acq lock
          setHasRanInitLockCheck(true);
        }
      };

      handleInitLockAcquire();
    }, [
      hasCollabPrereqs,
      hasRanInitLockCheck,
      isLockedAnywhere,
      isLockedByNXT,
      lockMutationStatus,
      lockQuerySuccess,
      recordUuid,
      tryAcqLockAndRefetch,
    ]);

    // Handle retry/reset of asset lock if api errors...
    // Should handle reset button in error views for
    // get lock query and/or set lock mutation failures.
    const assetLockQueryErrorReset = useCallback(async () => {
      await refetchAssetLockQuery();
      setHasRanInitLockCheck(false);
    }, [refetchAssetLockQuery]);

    // ------------------------------------------
    // Unlock asset if needed - on unload/leave
    // ------------------------------------------

    // Need ref to track unlocking in progress as both
    // pagehide and beforeunload events can fire at the same time
    // on a hard/soft nav away, and also component unmounts.
    const isDeletingLock = useRef(false);

    // Try delete lock keepalive with fallback to mutation
    const tryDeleteLock = useCallback(async () => {
      try {
        isDeletingLock.current = true;
        await deleteAssetIdLockKeepalive({
          assetId: recordUuid,
          force: false,
          fallbackFn: async (id) => {
            await assetUnlockMutationFnAsync({ path: { id: id } });
          },
        });
      } catch (error) {
        console.error('[tryDeleteLock] Error:', error);
      } finally {
        isDeletingLock.current = false;
      }
    }, [recordUuid, assetUnlockMutationFnAsync]);

    // Handle delete lock on unmount, soft nav, and hard navs
    useEffect(() => {
      // dont try to unlock if we have not
      // even attempted to lock yet...
      if (!hasRanInitLockCheck) {
        return;
      }
      // Only unlock if lock is held by NXT (collab or non-collab)
      const unlockIfNeeded = async () => {
        // prevent multiple delete lock calls
        if (unlockMutationStatus === 'pending') {
          return;
        }
        // eg both pagehide and beforeunload are fired
        // back to back on leaving in some cases...
        if (isDeletingLock?.current === true) {
          return;
        }
        try {
          const hasNxtLock = isLockedAnywhere && isLockedByNXT;
          const inCollabOrUserLock = hasCollabPrereqs || isLockedByThisUser;
          const shouldUnlock = hasNxtLock && inCollabOrUserLock;
          if (shouldUnlock) {
            await tryDeleteLock();
          }
        } catch (error) {
          console.error('[unlockIfNeeded] Error:', error);
        }
      };

      // try delete on unmount, soft nav, hard nav, etc...
      window.addEventListener('beforeunload', unlockIfNeeded);
      window.addEventListener('pagehide', unlockIfNeeded);
      return () => {
        unlockIfNeeded(); // unmounting unlock
        window.removeEventListener('beforeunload', unlockIfNeeded);
        window.removeEventListener('pagehide', unlockIfNeeded);
      };
    }, [
      hasCollabPrereqs,
      hasRanInitLockCheck,
      isLockedAnywhere,
      isLockedByNXT,
      isLockedByThisUser,
      tryDeleteLock,
      unlockMutationStatus,
    ]);

    // -----------------------------------------------------------------
    // Tiptap Collaboration Setup
    // https://docs.yjs.dev/api/y.doc
    // https://tiptap.dev/docs/collaboration/provider/integration
    // https://tiptap.dev/docs/hocuspocus/guides/collaborative-editing
    // -----------------------------------------------------------------

    // YDoc for TiptapCollabProvider + SyncedStores
    // Note: if collab enabled, this will be fresh until tiptap
    // onSynced event fires and loads the data to this ydoc?
    // This lets us have a YDoc instance in singleplayer mode for SyncedStores
    // https://react.dev/reference/react/useState#avoiding-recreating-the-initial-state
    const [yDoc, setYDoc] = useState(getNewYDoc);

    // Collaboration Provider State
    // undefined = disabled/undetermined, instance of TiptapCollabProvider = enabled
    const [collabProvider, setCollabProvider] = useState<CollabProviderState>(undefined);

    // Track live provider synced state (not initial sync)
    const [providerSynced, setProviderSynced] = useState<boolean>(false);

    // Update providerSynced state
    const updateProviderSyncedStatus = useCallback((synced: boolean) => {
      setProviderSynced(synced);
    }, []);

    // live provider connection status
    const [providerStatus, setProviderStatus] = useState<WebSocketStatus>(
      WebSocketStatus.Disconnected,
    );

    // Note: while disconnected provider can continuously try to reconnect
    // emitting a 'connecting' event repeatedly, may cause rerenders.
    // If this is an issue use a ref/state combo to update only if changed.
    const updateProviderStateStatus = useCallback((status: WebSocketStatus) => {
      setProviderStatus(status);
    }, []);

    // On provider synced store a config snapshot to a state here...
    // Used to determine overwrite ydoc with blue data or use collab doc
    // Should NOT be a reference to the live ydoc config, but a static initial snapshot of the config.
    // Null is undetermined - waiting for a synced event...
    // Else an object (can be empty if was never set) with the initial config.
    const [syncedInitialYDocConfig, setSyncedInitialYDocConfig] =
      useState<SyncedInitialYDocConfigState>(null);

    // Note: Have seen stale closures when reading state variables
    // inside of the providers callbacks (eg onSynced), but refs
    // seem to always be up to date. Use helper to update both concurently.
    const syncedInitYDocConfigValueRef = useRef<SyncedInitialYDocConfigState>(null);

    const updateConfigStateAndRef = useCallback(
      (config: SyncedInitialYDocConfigState) => {
        syncedInitYDocConfigValueRef.current = config;
        setSyncedInitialYDocConfig(config);
      },
      [setSyncedInitialYDocConfig],
    );

    // Base stable collab user info for tiptap extensions
    const collabUser = useMemo(() => {
      const baseCollabUser = buildCollabUserInfo({ userInfo });
      return baseCollabUser;
    }, [userInfo]);

    /**
     * 15s throttle awareness - for logrocket logging
     * See PresenceWidget / useDocumentPresence
     * for handling the presence UI
     */
    const onAwarenessChangeThrottled = useThrottledCallback(
      async (data: AwarenessUpdateInfo) => {
        console.log('[Provider: onAwarenessChange]*', data);
      },
      15000,
    );

    type ClearCollabEnvParams = {
      resetYDoc?: boolean;
    };

    // Teardown the collab environment and
    // optionally reset the local ydoc instance
    // prevent re-entrant calls to teardown process
    const isClearingRef = useRef(false);
    const clearCollabEnv = useCallback(
      ({ resetYDoc = false }: ClearCollabEnvParams) => {
        if (isClearingRef.current === true) {
          return;
        }
        if (collabProvider) {
          // mark as tearing down started
          isClearingRef.current = true;
          try {
            console.log('[clearCollabEnv] Teardown started...');
            collabProvider.disconnect();
            collabProvider.awareness?.destroy();
            collabProvider.document.destroy();
            collabProvider.destroy();
            yDoc.destroy();
            setCollabProvider(undefined);
            updateConfigStateAndRef(null);
            if (resetYDoc) {
              setYDoc(() => getNewYDoc());
            }
            console.log('[clearCollabEnv] Teardown finished...');
          } catch (error) {
            console.error('[clearCollabEnv] Teardown error:', error);
          } finally {
            // reset ref when complete
            isClearingRef.current = false;
          }
        }
      },
      [collabProvider, yDoc, updateConfigStateAndRef],
    );

    // React was not auto destroying the provider + ydoc on
    // unmounts / nav away, so ensure we do that here manually
    useEffect(() => {
      return () => {
        clearCollabEnv({ resetYDoc: false });
      };
    }, [clearCollabEnv]);

    /**
     * only try to setup the provider if we have the prereqs
     * for collab and we are not in a hard lockout or error state.
     * Ensure this doesnt start connecting midway through checking/acquiring locks
     * as if shouldEnableCollab flips true it will kick off connecting and initializing...
     */
    const shouldEnableCollab = useMemo(() => {
      return hasCollabPrereqs && hasRanInitLockCheck && !showLockout;
    }, [hasCollabPrereqs, showLockout, hasRanInitLockCheck]);

    // Determine if collaboration can be enabled
    const setupCollabProvider = useCallback(() => {
      // ensure collab check and required values present
      if (!shouldEnableCollab || !recordUuid || !hpAppId || !hpToken) {
        console.error('[setupCollabProvider] Missing prerequisites');
        return;
      }

      // If somehow any existing provider/ydoc config exists
      // cleanup and reset states to defaults, null, etc.
      if (collabProvider || syncedInitialYDocConfig) {
        console.log('[setupCollabProvider] Cleaning up existing environment...');
        clearCollabEnv({ resetYDoc: true });
      }

      /**
       *
       * Tiptap extends HocusPocus Provider
       * https://tiptap.dev/docs/collaboration/provider/integration
       * https://tiptap.dev/docs/collaboration/provider/events
       * https://tiptap.dev/docs/hocuspocus/provider/configuration
       *
       * It is easy to create subtle race conditions and hard to debug
       * issues if you are trying to listen to certain events,
       * such as the onSynced event, eg via collabProvider.on('synced', ...)
       * outside of the provider creation, as you may miss the event.
       *
       * The tiptap docs say: to track events immediately, pass event
       * listeners directly to the provider's constructor.
       * This guarantees that listeners are active from the start.
       *
       * The onSynced event is claimed to be only emitted once during the
       * lifetime of the provider, or when disconnected / reconnected
       * so any subsequent listeners may not recieve the initial event or
       * any indication that the event has already been emitted
       * when they attach to the provider.
       *
       * For that reason it is best to keep any and all onSynced
       * event handling directly on or near the provider creation.
       * One workaround can be to check if collabProvider?.isSynced in other
       * areas, but still may not recieve the event past the first emit.
       *
       * https://github.com/ueberdosis/hocuspocus/issues/883
       * https://github.com/ueberdosis/hocuspocus/issues/803
       *
       * Maybe: CS-8241 onsynced/resync duplication edge case.
       * Note there are reports that onSynced event can fire
       * more than once, contrary to what the docs say. Noticed
       * sometimes if leaving two tabs open for a long time, or
       * when writing a lot of data to ydoc while running
       * in localhost/dev mode, it seems to reconnect
       * and re-fire this event? Have seen onSynced fire
       * again when re-connecting eg: open two windows,
       * set one network offline, make text changes in both,
       * then set the offline one back online, and was able
       * to see the onSynced fire again.
       *
       * We dont want to update the intial config snapshot state
       * again if it was already set, as we need it once and stable
       * for our initial comparisons in editors/syncstore areas...
       *
       * https://tiptap.dev/docs/collaboration/provider/events
       *
       * Note: have seen stale closures when reading useState
       * states inside of constructor defined callbacks, even
       * when defining the callback handlers outside separately.
       * For this reason, recommend using a ref to store any values
       * that are needed. If your value also would need to trigger
       * a re-render, update both the state and ref concurrently,
       * callbacks should be reading from the ref value only.
       *
       * You can also register provider.on() / .off() inside
       * of a useEffect that has all correct dependencies,
       * after the provider is created, but confirm that
       * it doesnt fire/register more often than is expected...
       *
       * ---------------------------------------------------
       *
       * Note yAwareness:
       *
       * Previously collabUser data would be registered to the yjs
       * awareness protocol during editor mount and plugins setup, but
       * now that we need to track whole document presence, we must create
       * our own y-awareness instance and set user data immediately before
       * creating the provider, so that the first onAwarenessChange/update
       * events fired should contain the correct user metadata from the start.
       * This is neccesary for presence tracking to work correctly
       * with useDocumentPresence and buildPresenceMap calls, minimally
       * both userUuid and runtimeId must be present in all events.
       *
       */

      try {
        // Create an awareness instance for this ydoc
        const yAwareness = new awarenessProtocol.Awareness(yDoc);
        const nowIso = new Date().toISOString();

        // Setup initial starting awareness data user
        const initUserAwInfo = buildCollabUserInfo({
          userInfo: userInfo,
        });

        const initUserAwPresInfo = buildCollabUserPresenceInfo({
          userInfo: userInfo,
          runtimeId: runtimeId,
          isReadOnlyMode: isReadOnlyMode,
          lastActiveIso: nowIso,
        });

        const initAwPayload: TiptapAwarenessInfo = {
          user: initUserAwInfo,
          presence: initUserAwPresInfo,
        };

        // Update awareness once before creating provider
        // to help with initial awareness event data emit
        yAwareness.setLocalState(initAwPayload);

        // Create the collaboration provider
        const newProvider = new TiptapCollabProvider({
          name: recordUuid,
          appId: hpAppId,
          token: hpToken,
          user: initAwPayload?.user?.uuid,
          document: yDoc,
          awareness: yAwareness,
          // Debug logging for logrocket
          onOpen(data) {
            console.log('[Provider: onOpen]:', data);
          },
          onClose() {
            console.log('[Provider: onClose] Closed...');
          },
          onStatus(data) {
            console.log('[Provider: onStatus]', data);
            updateProviderStateStatus(data.status);
          },
          onDisconnect(data) {
            console.log('[Provider: onDisconnect]', data);
          },
          onConnect() {
            console.log('[Provider: onConnect] Connected...');
          },
          onDestroy() {
            console.log('[Provider: onDestroy] Destroyed...');
          },
          onAuthenticated() {
            console.log('[Provider: onAuthenticated] Authenticated...');
          },
          onAuthenticationFailed(data) {
            console.log('[Provider: onAuthenticationFailed]', data);
          },
          onAwarenessChange(data) {
            onAwarenessChangeThrottled(data);
          },
          onSynced(params) {
            try {
              // params state boolean indicates if synced or not
              // eg offline: false, online: true
              // NOTE: Contrary to the docs, this event may fire multiple
              // times throughout providers lifecycle

              const synced = params?.state;

              // Update live synced state
              updateProviderSyncedStatus(synced);

              // return if not synced, else log and continue
              // to handle the first synced event config snapshot
              if (!synced) {
                console.log('[Provider: onSynced] Synced is false...');
                return;
              } else {
                console.log('[Provider: onSynced] Synced is true...');
              }

              // should be same ydoc by reference as ours in state
              const thisYDoc = this?.document;
              if (!thisYDoc) {
                console.warn('[Provider: onSynced] Missing ydoc onSynced...');
                return;
              }

              // See comments above tiptap provider, need to guard this to
              // ensure snapshot only happens once on first good synced event..
              // NOTE: Check refs, not state due to stale closure issues.
              const hasSnapshot = Boolean(syncedInitYDocConfigValueRef.current);

              // If we already have a snapshot, dont overwrite it
              // as we only want the initial config state on first synced
              if (hasSnapshot) {
                console.log('[Provider: onSynced] State already set, skipping.');
                return;
              }

              // Looks weird, getArray, then a kv map, then set kv to arr...
              // but is the recommended way via y-utility docs for perf reasons.
              // see: https://github.com/yjs/y-utility?tab=readme-ov-file#ykeyvalue

              // get snapshot of ydoc config - note the first time a doc is
              // created in collab the config object can be undefined
              const yarr = thisYDoc.getArray<NxtYArrConfig>(NXT_YDOC_CONFIG);
              const ykv = new YKeyValue<NxtYDocConfig>(yarr);
              const yNxtConfig = ykv.get(NXT_YDOC_CONFIG);
              // if config exists deep copy it so we dont
              // accidentally store a reference to live config
              // if was undefined, set the configSnapshot to empty obj
              const configSnapshot = yNxtConfig ? clone(yNxtConfig) : {};

              // logging for logrocket review purposes
              console.log(
                '[Provider: onSynced] YDoc RevID:',
                configSnapshot?.ydoc_revision_id,
                'Record RevID:',
                recordRevId,
              );

              // Update the state and ref values concurrently
              // The ref will be used for callbacks, and the state
              // is needed to trigger any re-renders that depend on it.
              updateConfigStateAndRef(configSnapshot);

              // Send one additional awareness update on
              // the first synced event for good measure
              yAwareness.setLocalState(initAwPayload);
            } catch (error) {
              console.error('[Provider: onSynced]', error);
            }
          },
        });

        // Set provider to state after creation
        setCollabProvider(newProvider);
      } catch (error) {
        console.error('[setupNewCollabProvider] Error:', error);
      }
    }, [
      clearCollabEnv,
      collabProvider,
      hpAppId,
      hpToken,
      isReadOnlyMode,
      onAwarenessChangeThrottled,
      recordRevId,
      recordUuid,
      runtimeId,
      shouldEnableCollab,
      syncedInitialYDocConfig,
      updateConfigStateAndRef,
      updateProviderStateStatus,
      updateProviderSyncedStatus,
      userInfo,
      yDoc,
    ]);

    // Create collaboration provider
    useEffect(() => {
      if (!shouldEnableCollab) {
        return; // Exit if collab disabled
      }

      if (!recordUuid) {
        return; // Exit without record uuid
      }

      // First load - prev record undefined, and current has a uuid
      // as prev and current both initialized as undefined until
      // the first record fetch completes, which updates the state to
      // prev: undefined, current: record, which should be the first load.
      // Additionally to prevent infinite loop as the init sets the state
      // causing this to effect to re-run, check the provider is not already set
      const isFirstLoad = Boolean(!prevRecordUuid && recordUuid);
      const noProvider = Boolean(!collabProvider);
      if (isFirstLoad && noProvider) {
        setupCollabProvider();
      }

      // Edge case: record uuid changes without a remount... should be very rare.
      // I tested this manually and it should not occur as the edit view key changes
      // when the url/uuid changes, causing a full remount of the edit view, but
      // if for any reason the loaded record uuid changes while mounted,
      // we must reinitialize the provider to connect to the correct doc
      // eg. some article-to-article soft nav without, a remount of this component.
      const hadPrevRecord = Boolean(prevRecordUuid);
      const recordUuidChanged = hadPrevRecord && prevRecordUuid !== recordUuid;
      const collabProviderName = collabProvider?.configuration?.name; // should be the bloxdoc uuid...
      const collabNameMismatch = collabProviderName !== recordUuid;
      if (recordUuidChanged && collabNameMismatch) {
        // warning for logrocket/devs...
        console.warn('Record UUID changed without remount:', prevRecordUuid, recordUuid);
        setupCollabProvider();
      }
    }, [
      shouldEnableCollab,
      prevRecordUuid,
      recordUuid,
      collabProvider,
      setupCollabProvider,
    ]);

    // Reset lock acquisition states if record uuid changes
    // getAssetIdLock query key should trigger a refetch on uuid change
    useEffect(() => {
      const hadPrevRecord = Boolean(prevRecordUuid);
      const recordUuidChanged = hadPrevRecord && prevRecordUuid !== recordUuid;
      if (recordUuidChanged) {
        setHasRanInitLockCheck(false);
      }
    }, [prevRecordUuid, recordUuid]);

    // -----------------------------------------------------------------
    // Collaboration State
    // -----------------------------------------------------------------

    // Determine if we're in collaboration mode
    const isCollabMode = useMemo(() => {
      return Boolean(collabProvider && syncedInitialYDocConfig);
    }, [collabProvider, syncedInitialYDocConfig]);

    // -----------------------------------------------------------------
    // Templates
    // -----------------------------------------------------------------

    const isTemplate = useMemo(() => {
      return recordCopy?.template === true;
    }, [recordCopy?.template]);

    const isDeleted = useMemo(
      () => isAssetActuallyDeleted(recordCopy, siteTimeZone),
      [recordCopy, siteTimeZone],
    );

    /**
     * -----------------------------------------------------------
     * Workflows
     * -----------------------------------------------------------
     *
     * These states are setup to be changed programmatically and
     * follow the workflow/process values that are in syncstore.
     *
     * Syncstore is the source of truth for workflow/process ids.
     * However, as we dont have access to syncstore at this level
     * in context, we will initialize from the record to begin.
     *
     * When RightSide.tsx mounts and syncstore updates, it will
     * update these states (and form states) in a useEffect,
     * keeping these states in sync with the syncstore values.
     *
     */

    const hasInitWfViaRecord = useRef<boolean>(false);
    const [workflowId, setWorkflowId] = useState<string | null>(null);
    const [workflowProcessId, setWorkflowProcessId] = useState<string | null>(null);

    // Reset states if record uuid changes
    useEffect(() => {
      setWorkflowId(null);
      setWorkflowProcessId(null);
      hasInitWfViaRecord.current = false;
    }, [recordUuid]);

    // Intial setup from record on first load
    // After init, states are synced via syncstore (RightSide.tsx)
    useEffect(() => {
      if (!assetFormDefaultValues) {
        return;
      }
      if (hasInitWfViaRecord.current) {
        return;
      }
      const recWfId = assetFormDefaultValues.properties?.workflow_id ?? null;
      const recWfProcId = assetFormDefaultValues.properties?.workflow_process_id ?? null;
      setWorkflowId(recWfId);
      setWorkflowProcessId(recWfProcId);
      hasInitWfViaRecord.current = true;
    }, [assetFormDefaultValues]);

    // Current workflow - (null if no workflow / not found)
    const currentWorkflow: Workflow | null = useMemo(() => {
      if (!siteWorkflows || !Array.isArray(siteWorkflows)) {
        return null;
      }
      const currWf = siteWorkflows?.find((wf) => wf?.id === workflowId) ?? null;
      return currWf;
    }, [siteWorkflows, workflowId]);

    // Current workflow process - (null if no process / not found)
    const currentWorkflowProcess: WorkflowProcess | null = useMemo(() => {
      if (!currentWorkflow || !Array.isArray(currentWorkflow?.processes)) {
        return null;
      }
      const currProc =
        currentWorkflow?.processes?.find((pr) => pr?.id === workflowProcessId) ?? null;
      return currProc;
    }, [currentWorkflow, workflowProcessId]);

    // The current workflow process group string (or null)
    // If no workflow this should be null
    const currentProcessGroup: string | null = useMemo(() => {
      return currentWorkflowProcess?.group ?? null;
    }, [currentWorkflowProcess]);

    // Is user in the workflow process group?
    // Important: If no workflow / no group, treat as true/allowed.
    // Else check users group strings includes the wf proc group string.
    const inCurrentProcessGroup: boolean = useMemo(() => {
      if (!currentProcessGroup) {
        return true;
      }
      const userInGroup = Boolean(userGroups?.includes(currentProcessGroup));
      return userInGroup;
    }, [currentProcessGroup, userGroups]);

    // -----------------------------------------------------------------
    // Permissions Checks / Workflow Group (read only states)
    // -----------------------------------------------------------------

    // Translated strings
    // "Read Only", "Requires modify permission", "Restricted by workflow"...
    const strReadOnly = t('blox.general.readonly');
    const strNoModifyPerm = t('blox.permissions.msg.required_short', {
      name: modifyPermName,
    });
    const strWfRestricted = t('blox.editorial.metadata.group.workflow.help.restricted');
    const strDeletedAsset = `${t('blox.editorial.deleted.banner')} ${strReadOnly}`;

    // Check and update readOnlyState based on permissions + workflow group
    // eg: no perms, or workflow group transition restrictions
    useEffect(() => {
      // Effect only applies for edit mode
      if (assetUpdateMode !== 'edit') {
        return;
      }

      // Skip if user continued from the lockout screen.
      // Effect should not override api locked state.
      if (readOnlyState.reason === 'lockout_view') {
        return;
      }

      // Calculate next state update
      let newState: ReadOnlyState;

      // Deleted assets are always read-only
      if (isDeleted) {
        newState = {
          isReadOnly: true,
          message: strDeletedAsset,
          reason: 'deleted',
        };
      } else if (userIsAdmin) {
        // Admins are always allowed, bypass all checks
        newState = {
          isReadOnly: false,
          message: '',
          reason: null,
        };
      } else if (!userCanModify) {
        // Non-admin - no modify assets permission
        newState = {
          isReadOnly: true,
          message: `${strReadOnly} ${emdash} ${strNoModifyPerm}`,
          reason: 'permissions',
        };
      } else if (currentProcessGroup && !inCurrentProcessGroup) {
        // Has modify permissions, but workflow group restricted
        newState = {
          isReadOnly: true,
          message: `${strReadOnly} ${emdash} ${strWfRestricted}`,
          reason: 'permissions',
        };
      } else {
        // Has modify permission and passes workflow group (or no group)
        newState = {
          isReadOnly: false,
          message: '',
          reason: null,
        };
      }

      // Nothing changed, skip update
      if (isDeepEqual(readOnlyState, newState)) {
        return;
      }

      // Apply state update
      setReadOnlyState(newState);
    }, [
      assetUpdateMode,
      currentProcessGroup,
      inCurrentProcessGroup,
      isDeleted,
      readOnlyState,
      strDeletedAsset,
      strNoModifyPerm,
      strReadOnly,
      strWfRestricted,
      userCanModify,
      userIsAdmin,
    ]);

    // -----------------------------------------------------------------
    // Memoized context value
    // -----------------------------------------------------------------
    const contextValue = useMemo(() => {
      return {
        recordCopy,
        prevRecordCopy,
        recordType,
        assetFormDefaultValues,
        assetUpdateMode,
        isEditingMode,
        isImportMode,
        isCreateMode,
        collabProvider,
        collabUser,
        yDoc,
        syncedInitialYDocConfig,
        providerSynced,
        providerStatus,
        readOnlyState,
        setReadOnlyState,
        isReadOnlyMode,
        isTemplate,
        isDeleted,
        isCollabMode,
        workflowId,
        setWorkflowId,
        workflowProcessId,
        setWorkflowProcessId,
        currentWorkflow,
        currentWorkflowProcess,
        currentProcessGroup,
        inCurrentProcessGroup,
      };
    }, [
      recordCopy,
      prevRecordCopy,
      recordType,
      assetUpdateMode,
      isEditingMode,
      isImportMode,
      isCreateMode,
      assetFormDefaultValues,
      collabProvider,
      collabUser,
      yDoc,
      providerSynced,
      providerStatus,
      syncedInitialYDocConfig,
      readOnlyState,
      setReadOnlyState,
      isReadOnlyMode,
      isCollabMode,
      isTemplate,
      isDeleted,
      workflowId,
      setWorkflowId,
      workflowProcessId,
      setWorkflowProcessId,
      currentWorkflow,
      currentWorkflowProcess,
      currentProcessGroup,
      inCurrentProcessGroup,
    ]);

    // See notes in AssetEditView onError handler about RA side effects
    // as well as the notes on this variable where it is defined.
    if (hasEditCtxError) {
      return (
        <ErrorAlerts
          showSeverity={true}
          showDetails={true}
          errors={[
            {
              status: `${raEditError?.axiosStatus}`,
              message: `${raEditError?.message}`,
              fullDetails: raEditError,
            },
          ]}
        />
      );
    }

    // Ensure edit/create/import mode has been determined and minimally
    // the form default values and record have been determined.
    // As form default values are derived from record copy state
    // we should be able to use it to determine if the context is ready to be rendered
    // as it would be fetched and set in edit/import mode, or has set defaults for create.
    if (!assetUpdateMode || !assetFormDefaultValues) {
      return (
        <SkeletonBox
          text="Loading Asset..."
          height="100%"
          width="100%"
        />
      );
    }

    // Error in case of check or set lock failure
    // Retry button should reset and rerun the
    // entire check and acquire lock process
    if (assetLockQueryError || lockMutationError) {
      const errsArr: ErrorDetails[] = [];

      if (assetLockQueryError) {
        errsArr.push({
          status: `${assetLockQueryError?.status || assetLockQueryError?.axiosStatus}`,
          message: `${assetLockQueryError?.message || assetLockQueryError?.axiosMessage}`,
          fullDetails: assetLockQueryError,
        });
      }

      if (lockMutationError) {
        errsArr.push({
          status: `${lockMutationError?.status || lockMutationError?.axiosStatus}`,
          message: `${lockMutationError?.message || lockMutationError?.axiosMessage}`,
          fullDetails: lockMutationError,
        });
      }

      return (
        <ErrorAlerts
          severity="warning"
          buttonText="Retry"
          buttonIcon={<ReplayIcon />}
          buttonOnClick={assetLockQueryErrorReset}
          showDetails={true}
          errors={errsArr}
        />
      );
    }

    // ensure that the asset lock query/mutations
    // do not rerun once we give access to the editor view
    // as it would cause the children to unmount
    // which can cause loss of editor states
    if (lockingProcessLoading) {
      return (
        <SkeletonBox
          text="Checking Locks..."
          height="100%"
          width="100%"
        />
      );
    }

    // If the asset is considered locked show the lockout component
    // The user may continue to a read-only view or go back...
    if (showLockout) {
      return (
        <AssetConfigContext.Provider value={contextValue}>
          <AssetLocked
            lockData={assetLockQueryData}
            isCollabMode={hasCollabPrereqs}
            userUuid={userUuid}
          />
        </AssetConfigContext.Provider>
      );
    }

    // In collab we should wait for a full sync of the collab provider
    // as we dont want the editors to mount in collab mode and start mutating
    // the ydoc upon first syncs, etc, inadvertently creating strange race conditions...
    // unforunately we may not have a lot of control over that behaviour as it is
    // automatically done once the collab extension is registered to the editor..
    // see more notes in useCreateEditor.tsx file / eof here
    const yConfigNotSynced = syncedInitialYDocConfig === null;
    // collab enabled, awaiting first ydoc config sync
    const collabSetupLoading = shouldEnableCollab && yConfigNotSynced;
    if (collabSetupLoading) {
      return (
        <SkeletonBox
          text="Synchronizing..."
          height="100%"
          width="100%"
        />
      );
    }

    return (
      <AssetConfigContext.Provider value={contextValue}>
        {children}
      </AssetConfigContext.Provider>
    );
  },
);
AssetConfigContextProvider.displayName = 'AssetConfigContextProvider';

/**
 * CMSADMIN-1213 - Asset Locking Initial
 * CMSADMIN-1434 - Collaborative locking
 * TNCMS-555797 - Collaborative Locking API
 *
 * Collaboration enabled:
 * If the asset is unlocked, then acquire a lock and continue.
 * If the asset is locked then we must check the lock origin
 * -- If the lock origin is nxt we can add our lock and continue
 * -- If the lock origin is not nxt render lockout / read-only mode
 *
 * Collaboration disabled:
 * If the asset is unlocked, then acquire a lock and continue.
 * If the asset is locked (anywhere) render lockout / read-only mode
 * -- Except if the origin is NXT and the current user is the lock owner
 * -- Then allow, but dont need to reacquire the lock.
 *
 * -------------------------------------------
 * Asset Unlocking Notes
 * -------------------------------------------
 *
 * General Unlocking:
 * There are various edge case issues with the way unlocking works
 * and have been documented in the notes of the above tickets.
 *
 * However there are various issues with 'unlock on leave' attempts if
 * trying to do this entirely client side without some heartbeat or server check.
 * Detecting browser tab close events is very unreliable and deprecated
 * - No good solution here per MDN docs: https://developer.mozilla.org/en-US/docs/Web/API/Window/unload_event
 * - Alternatives are visibilitychange and pagehide event. Would need to workshop how
 * - long a tab could be hidden or idle for before we consider it a full leave, if at all.
 * Users leave tabs open so the client may never try to call for unlock
 * Sending out an API call on unmount is not reliable as we cant await a response and handle errors.
 * - A user could be offline and close their browser or try to nav, unlock call would never be sent.
 * - If they were online, can partially workaround the await issue by moving the unlock call to
 * - a component way high up in the tree thats unlikely to unmount, but its still not perfect.
 *
 * In collaboration mode:
 * The Awareness API sometimes glitches out and desyncs user presence (see CMSADMIN-1157)
 * it also does not take into account offline/idle users which may not have saved or finished working.
 * - I have not found good frontend only solve as long as we rely on awareness api...
 * - The next best option would be something like a heartbeat to the server in which
 * - if the client doesnt respond for a certain amount of time the lock is auto released.
 *
 * In non-collaboration mode:
 * A single user might have multiple tabs open of the same asset and so
 * unfortunatley without a more robust solution, the first tab/nav away
 * would need to release the lock.
 * - Per discussion we have to do it like blue does and just unlock it.
 * - Potentially could come up with a BroadcastChannel solution for this
 * - but it wouldnt account for the same user or multiple devices either.
 *
 */

/**
 * CMSADMIN-1157
 * -------------------------------------------
 * Notes on Collab Sync / Initial YDoc Content
 * -------------------------------------------
 *
 * The majority of literature on the web strongly indicates that
 * seeding the yjs document should only be done once from json/plaintext
 * and then in perpetuity from that point forward we should be storing
 * the yjs binary/state in our database, not the json/plaintext content.
 * We can store a json/plaintext alongside the yjs binary, but for true
 * collaboration capabilities, the yjs format is required.
 *
 * Without us storing the actual yjs binary (or some representation of it)
 * we will perpetually run into the problems of 'who wins' / 'content loss',
 * and having desynced states between nxt and other platforms.
 *
 * The main issue is that if we determine the need to overwrite the
 * ydoc with the bloxdoc content, this has no represnetation of state
 * changes and thus to the end user it will appear as if their changes
 * were lost when that overwrite occurs.
 *
 * The current workaround we are using is storing a hidden field
 * on the ydoc in config that compares the yjs last_update,
 * which we update upon any changes to the ydoc versus
 * the bloxdoc update_time and making the determination
 * on whether or not to overwite the ydoc, in useCreateEditor.
 *
 * A user can still potentially "lose content" with this mechanism
 * until some form of hard asset locking is in place, for example someone in
 * NXT is editing an article, and goes idle without saving. Then someone
 * in blue opens that same article, makes changes, saves it, and opens
 * it in NXT. Since the last updated time of the ydoc is now older
 * than the blue doc, the moment the doc is loaded in NXT the ydoc
 * will be overwritten with the blue content, and the NXT users unsaved
 * changes will be wiped out.
 *
 * References:
 * -- "A simple JSON document is not enough to merge changes."
 * -- "Do not be tempted to store the YDoc as JSON and recreate it as YJS binary when the user connects.
 * -- This will cause issues with merging of updates and content will duplicate on new connections.
 * -- The data must be stored as binary to make use of the YJS format."
 * https://github.com/yjs/yjs
 * https://tiptap.dev/docs/guides/output-json-html#option-3-yjs
 * https://tiptap.dev/docs/hocuspocus/server/examples#php--laravel
 * https://discuss.yjs.dev/t/issue-with-initial-data-duplication-in-collaborative-editor/2170
 * https://discuss.yjs.dev/t/thinking-the-yjs-way/731
 * https://discuss.yjs.dev/t/appropriate-way-to-load-initial-data-fallback-to-current-yjs-doc-data/1189/6
 * https://github.com/yjs/yjs-demos/issues/16
 *
 * Tiptap offers a rest API that we could potentially use?
 * https://tiptap.dev/docs/collaboration/documents/rest-api
 *
 *
 * Given these warnings, we have some mitigations for now:
 *
 * If we really need to avoid the initial setContent calls
 * that the Collaboration extension does automatically
 * we can theoretically update the ydoc directly and ensure
 * that it happens before the editor is initialized.
 *
 * There is a set of utilities that y-prosemirror provides
 * prosemirrorToYDoc, prosemirrorJSONToYDoc (and vice versa)
 * And some other utitlies that tiptap/prosemirror provides
 * prosemirrorJSONToYXmlFragment, generateJSON, getSchema
 * https://github.com/yjs/y-prosemirror
 * https://tiptap.dev/docs/editor/api/utilities/html
 * https://tiptap.dev/docs/editor/core-concepts/schema#get-the-underlying-prosemirror-schema
 *
 * The only concerns with this are that the tiptap-prosemirror
 * conversions may not be one-to-one, and there are concerns
 * about making a mistake in the ydoc and such having state of
 * the ydoc becoming invalid... would need to dig deeper on that.
 *
 * So it may be worth setting up a 'dummy' editor instance
 * to get the schema, and json content, and use methods on
 * that dummy editor to get the data we need and then
 * update the ydoc with that data.
 *
 * It is probably safest to just let the useCreateEditor setContent
 * onCreate, as then we know that tiptap has formatted the
 * content properly to whatever format the ydoc expects.
 * But user may see some flash of old content until collab syncs
 * as the hook determines what to do with the content state.
 *
 * The create editor hook would break if tiptap ever removed the "y-sync$"
 * attribute from the meta on the transaction. So leaving this comment
 * here as a backup plan if we ever need to go this route.
 *
 * Some pseudocode could be:
 * If we detect the bloxDoc is newer than the collab doc
 * we could update the ydoc with the bloxdoc content
 *
 * Use some state to block providing ctx/render until these operations
 * complete for each field that we have. Also, set the revision id
 * on the ydoc config during the process. Can use the
 * bloxdoc revision id since thats whats being synced into the ydoc.
 *
 * Additionally, if going this route, we could ideally remove all of the
 * initial setContent workarounds in the useCreateEditor hook, and simply
 * leave in the parts in that update the nxt_ydoc_config map onChange.
 *
 * Could create a helper for us to manually mutate the ydoc before mounting editors...
 * and in some useEffect, detect once synced and and in collab mode and flip the state once ready.
 *
 * updateYDocManually({ provider, bloxdoc, providerIsSynced, ydocConfigInitialState, etc... }) {
 *   if (!synced) return;
 *   if (!provider.ydoc) return;
 *   if (!bloxdoc) return;
 *   if (ydoc revision id is same or newer) return;
 *   const nowISO = new Date().toISOString();
 *   ydoc.transact(() => {
 *     for (const fieldKey of Object.keys(editorConfigsMap)) {
 *     const getConfig = editorConfigsMap[fieldKey];
 *     const editorConfig = getConfig(bloxdoc);
 *     const initialContent = editorConfig?.contentForEditor;
 *     const dummyEditor = createDummyEditor(...);
 *     const schema = dummyEditor.schema;
 *     const pmJSON = dummyEditor.getJSON();
 *     const newFragment = prosemirrorToYXmlFragment(schema, pmJSON);
 *     const yDocFieldFragment = ydoc.getXmlFragment(fieldKey);
 *     yFieldFragment.delete(0, yFieldFragment.length);
 *     yDocFieldFragment = newFragment;
 *     // ^ set the new fragment - in some basic testing, direct assignment worked fine?
 *     // you may want to verify that if theres an official setter method we should use https://docs.yjs.dev/
 *     dummyEditor.destroy();
 *    console.debug(`Updated YDoc fragment for field '${fieldKey}'.`);
 *  }
 *  // Finally update the global config maps on the ydoc.
 *  const configMap = ydoc.getMap<NxtYDocConfig>('nxt_ydoc_config');
 *  configMap.set('ydoc_revision_id', bloxdoc-revision-id);
 *  });
 * // flip some react state and allow to render / provide ctx
 *  setYDocReady(true);
 * }
 *
 **/
